import { DOMINIO_EMAIL } from './reglas';

/**
 * Todos los textos de ayuda del formulario de empleados, en un solo sitio. Lo que el responsable aún no ha confirmado
 * lleva `confirmada: false` y sale en pantalla con «(por confirmar)»: se corrige aquí y la marca desaparece sola.
 */
export const POR_CONFIRMAR = '(por confirmar)';

interface Ayuda { texto: string; confirmada: boolean }

/** Conserva las claves literales y obliga a que cada valor sea una `Ayuda`. */
const definir = <T extends Record<string, Ayuda>>(ayudas: T): T => ayudas;

const AYUDAS = definir({
  nombreCompleto: { texto: 'Nombre y apellidos. Obligatorio.', confirmada: true },
  nombreAbreviado: { texto: 'Versión corta del nombre. Opcional.', confirmada: true },
  codigoCorto: { texto: 'Hasta 5 caracteres. Opcional.', confirmada: true },
  emailEmpresarial: { texto: `Obligatorio, de ${DOMINIO_EMAIL.slice(1)}. No puede repetirse entre empleados activos.`, confirmada: true },
  puesto: { texto: 'Puesto en la empresa. Opcional.', confirmada: true },
  departamento: { texto: 'Área en la que trabaja. Opcional.', confirmada: true },
  telefono: { texto: 'Teléfono corporativo, hasta 20 caracteres. Opcional.', confirmada: true },
  fechaAlta: { texto: 'Sin fecha, se da de alta hoy. Después no se puede cambiar.', confirmada: true },
  idDelegacion: { texto: 'Obligatoria. Si la cambias, se guarda el histórico.', confirmada: true },
  idZona: { texto: 'Opcional. Solo salen las zonas de la delegación elegida.', confirmada: true },
  nivelSeguridad: { texto: 'Nivel de acceso del empleado en Gescom. Obligatorio, número entero.', confirmada: false },
  nivelAsesor: { texto: 'Nivel del empleado como asesor. Opcional, número entero.', confirmada: false },
  nivelInterlocucion: { texto: 'Opcional: bajo, medio o alto.', confirmada: true },
  accesoGdgo: { texto: 'Permite al empleado entrar en GDGO, el CRM del móvil.', confirmada: true },
  // D-OP-24: son asignables los empleados activos con esta marca.
  asignarCcl: { texto: 'Puede ser responsable de oportunidades: sale entre los empleados asignables.', confirmada: true },
  controlAcompaniamiento: { texto: 'Activa el control de acompañamiento de este empleado.', confirmada: false },
  roles: { texto: 'Cada rol es un grupo de Entra. Los que no están en el catálogo salen como «Sin nombre». Entre paréntesis, cuántos empleados lo tienen.', confirmada: true },
});

export type ClaveAyuda = keyof typeof AYUDAS;

/** La ayuda de un campo, con «(por confirmar)» al final si el responsable aún no la ha validado. */
export const ayuda = (clave: ClaveAyuda): string => {
  const { texto, confirmada } = AYUDAS[clave];
  return confirmada ? texto : `${texto} ${POR_CONFIRMAR}`;
};

/** Las claves cuyo texto sigue sin confirmar. */
export const ayudasPorConfirmar = (): ClaveAyuda[] =>
  (Object.keys(AYUDAS) as ClaveAyuda[]).filter((c) => !AYUDAS[c].confirmada);

export const CLAVES_AYUDA = Object.keys(AYUDAS) as ClaveAyuda[];

/** Aviso de las pantallas que cambian roles: la tabla de Gescom no es la pertenencia a los grupos de Entra. */
export const AVISO_ROLES = {
  titulo: 'Solo cambia la tabla de Gescom.',
  texto: 'Editar los roles aquí no cambia la pertenencia a los grupos de Entra.',
};

/** En la edición los roles no van en el formulario: se cambian desde su propio botón de la ficha. */
export const ROLES_EN_EDICION = 'Los roles se cambian con el botón «Roles» de la ficha.';
